import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["toggleButton", "modal", "modalFilterBadge"]

  connect() {
    this.handleKeydown = this.handleKeydown.bind(this)
    this.modalActiveFilter = null
    this.setupSidebarScrollFix()
  }

  disconnect() {
    // Remove keyboard listener
    document.removeEventListener("keydown", this.handleKeydown)
    this.teardownSidebarScrollFix()
  }

  setupSidebarScrollFix() {
    const sidebar = this.element.querySelector('[data-controller*="activity-template-sidebar"]')
    if (!sidebar) return

    this._sidebarWheelHandler = (e) => {
      const scrollable = sidebar.querySelector('[class*="overflow-y-auto"]')
      if (!scrollable) return

      const { scrollTop, scrollHeight, clientHeight } = scrollable
      const maxScroll = scrollHeight - clientHeight
      if (maxScroll <= 0) return

      const delta = e.deltaY
      const atTop = scrollTop <= 0 && delta < 0
      const atBottom = scrollTop >= maxScroll && delta > 0

      if (!atTop && !atBottom) {
        scrollable.scrollTop += delta
        e.preventDefault()
        e.stopPropagation()
      }
    }

    sidebar.addEventListener('wheel', this._sidebarWheelHandler, { passive: false })
  }

  teardownSidebarScrollFix() {
    const sidebar = this.element.querySelector('[data-controller*="activity-template-sidebar"]')
    if (sidebar && this._sidebarWheelHandler) {
      sidebar.removeEventListener('wheel', this._sidebarWheelHandler)
      this._sidebarWheelHandler = null
    }
  }

  handleSidebarClosed() {
    // Show the toggle button in header
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.remove("hidden")
    }
  }

  handleSidebarOpened() {
    // Hide the toggle button in header
    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.classList.add("hidden")
    }
  }

  openSidebar() {
    // Find all activity template sidebar elements and call their open method
    const sidebarElements = document.querySelectorAll('[data-controller*="activity-template-sidebar"]')

    sidebarElements.forEach(element => {
      const controller = this.application.getControllerForElementAndIdentifier(
        element,
        'activity-template-sidebar'
      )
      if (controller && controller.open) {
        controller.open()
      }
    })
  }

  openModal(event) {
    // Store the phase_id from the button that was clicked
    const phaseId = event.currentTarget.dataset.phaseId
    if (phaseId) {
      this.currentPhaseId = phaseId
    }

    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      document.body.style.overflow = "hidden"
      // Add keyboard listener for ESC key
      document.addEventListener("keydown", this.handleKeydown)
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      document.body.style.overflow = ""
      // Remove keyboard listener
      document.removeEventListener("keydown", this.handleKeydown)
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      this.closeModal()
    }
  }

  closeModalBackdrop(event) {
    // Close modal if clicking on backdrop (not the modal content)
    if (event.target === event.currentTarget) {
      this.closeModal()
    }
  }

  stopPropagation(event) {
    // Prevent clicks inside modal from closing it
    event.stopPropagation()
  }

  filterModal(event) {
    const value = event.currentTarget.dataset.filterValue
    const isActive = this.modalActiveFilter === value

    this.modalActiveFilter = isActive ? null : value

    this.modalFilterBadgeTargets.forEach(badge => {
      const selected = badge.dataset.filterValue === this.modalActiveFilter
      badge.classList.toggle("border-[#5a36e6]", selected)
      badge.classList.toggle("bg-[#f5f3ff]", selected)
      badge.classList.toggle("text-[#5a36e6]", selected)
      badge.classList.toggle("border-[#E4E5E8]", !selected)
      badge.classList.toggle("bg-[#FAFAFB]", !selected)
      badge.classList.toggle("text-[#5F6672]", !selected)
    })

    if (this.hasModalTarget) {
      const cards = this.modalTarget.querySelectorAll("[data-template-badge]")
      cards.forEach(card => {
        if (!this.modalActiveFilter || card.dataset.templateBadge === this.modalActiveFilter) {
          card.classList.remove("hidden")
        } else {
          card.classList.add("hidden")
        }
      })
    }
  }

  selectTemplate(event) {
    const card = event.currentTarget
    const activityData = {
      title: card.dataset.activityTitle,
      type: card.dataset.activityType,
      description: card.dataset.activityDescription,
      badge: card.dataset.activityBadge,
      icon: card.dataset.activityIcon
    }

    // Find the activity-drag-drop controller
    const dragDropElement = document.querySelector('[data-controller*="activity-drag-drop"]')
    if (dragDropElement) {
      const dragDropController = this.application.getControllerForElementAndIdentifier(
        dragDropElement,
        'activity-drag-drop'
      )

      if (dragDropController) {
        // Find the dropzone for the specific phase
        let dropzone
        if (this.currentPhaseId) {
          // Find the phase container with this phase_id
          const phaseContainer = document.querySelector(`[data-phase-id="${this.currentPhaseId}"]`)
          if (phaseContainer) {
            dropzone = phaseContainer.querySelector('[data-activity-drag-drop-target="dropzone"]')
          }
        }

        // Fallback to first dropzone if phase-specific dropzone not found
        if (!dropzone) {
          dropzone = dragDropElement.querySelector('[data-activity-drag-drop-target="dropzone"]')
        }

        if (dropzone) {
          // Add the activity card
          dragDropController.addActivityCard(activityData, dropzone)

          // Close the modal
          this.closeModal()
        }
      }
    }
  }
}
